import React, { useState, useEffect } from "react";
import { MessageSquare, Mail, User, Calendar, Trash2, CheckCircle, Search, Loader2 } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const API_URL = (import.meta.env.VITE_API_URL || "http://localhost:3001") + "/api/admin";

export default function SupportManager() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const token = localStorage.getItem("ka_admin_token");

  const fetchMessages = async () => {
    try {
      const response = await fetch(`${API_URL}/support`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setMessages(data);
    } catch (err) {
      toast.error("Failed to fetch support messages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, [token]);

  const handleDelete = async (id) => {
    if (!confirm("Are you sure you want to delete this message?")) return;
    try {
      const res = await fetch(`${API_URL}/support/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast.success("Message deleted");
      setMessages(prev => prev.filter(m => m.id !== id));
    } catch {
      toast.error("Failed to delete message");
    }
  };

  const handleMarkRead = async (id) => {
    try {
      const res = await fetch(`${API_URL}/support/${id}/read`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      setMessages(prev => prev.map(m => m.id === id ? { ...m, is_read: 1 } : m));
      toast.success("Marked as read");
    } catch {
      toast.error("Failed to mark as read");
    }
  };

  const filteredMessages = Array.isArray(messages) ? messages.filter(msg => 
    msg.name?.toLowerCase().includes(search.toLowerCase()) || 
    msg.email?.toLowerCase().includes(search.toLowerCase()) || 
    msg.subject?.toLowerCase().includes(search.toLowerCase())
  ) : [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
        <h3 className="text-xl font-bold">Contact Support Messages</h3>
        <div className="relative w-full md:w-96">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input 
            placeholder="Search messages..." 
            className="pl-10 bg-white/5 border-[#8B0000]/30 focus:border-[#8B0000]/60 text-white"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-10 h-10 text-[#8B0000] animate-spin" />
        </div>
      ) : filteredMessages.length === 0 ? (
        <div className="text-center py-20 bg-white/5 border border-dashed border-[#8B0000]/20 rounded-lg">
          <MessageSquare className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500">No support messages found.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {filteredMessages.map((msg) => (
            <Card key={msg.id} className="bg-[#1a1a1a]/80 border-[#8B0000]/30 overflow-hidden relative group">
              <div className="flex flex-col md:flex-row">
                <div className="md:w-64 p-6 border-r border-[#8B0000]/10 space-y-3 bg-black/20">
                  <div className="flex items-center gap-2 text-sm text-gray-300">
                    <User className="w-4 h-4 text-[#8B0000]" />
                    <span className="font-bold">{msg.name}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <Mail className="w-4 h-4 text-[#8B0000]" />
                    <span className="truncate">{msg.email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Calendar className="w-4 h-4 text-[#8B0000]" />
                    <span>{new Date(msg.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="pt-2">
                    {msg.is_read ? (
                      <Badge variant="outline" className="border-green-500 text-green-500 bg-green-500/5">Read</Badge>
                    ) : (
                      <Badge variant="outline" className="border-red-500 text-red-500 animate-pulse bg-red-500/5">New</Badge>
                    )}
                  </div>
                </div>
                <div className="flex-1 p-6 relative">
                  <h4 className="text-lg text-white font-bold mb-3 flex items-center gap-2">
                    {msg.subject || "No Subject"}
                  </h4>
                  <p className="text-gray-400 text-sm whitespace-pre-wrap leading-relaxed">
                    {msg.message}
                  </p>
                  <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    {!msg.is_read && (
                      <Button size="icon" variant="ghost" onClick={() => handleMarkRead(msg.id)} className="text-green-500 hover:text-green-400 hover:bg-green-900/10" title="Mark as read">
                        <CheckCircle className="w-4 h-4" />
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => handleDelete(msg.id)} className="text-red-500 hover:text-red-400 hover:bg-red-900/10" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
